import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/auth/useAuth';

interface ResumeScoreFormProps {
  reviewId: string;
  userId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function ResumeScoreForm({ reviewId, userId, onClose, onSuccess }: ResumeScoreFormProps) {
  const [scores, setScores] = useState({
    keywordScore: 0,
    formatScore: 0,
    contentScore: 0,
    atsScore: 0
  });
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();

  const calculateTotalScore = () => {
    const weights = {
      keywordScore: 0.25,
      formatScore: 0.25,
      contentScore: 0.30,
      atsScore: 0.20
    };

    return Math.round(
      scores.keywordScore * weights.keywordScore +
      scores.formatScore * weights.formatScore +
      scores.contentScore * weights.contentScore +
      scores.atsScore * weights.atsScore
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    try {
      // Get current iteration number
      const { data: existingScores } = await supabase
        .from('resume_scores')
        .select('iteration_number')
        .eq('resume_id', reviewId)
        .order('iteration_number', { ascending: false })
        .limit(1);

      const iterationNumber = existingScores?.length ? existingScores[0].iteration_number + 1 : 1;

      const { error } = await supabase
        .from('resume_scores')
        .insert({
          resume_id: reviewId,
          user_id: userId,
          total_score: calculateTotalScore(),
          keyword_score: scores.keywordScore,
          format_score: scores.formatScore,
          content_score: scores.contentScore,
          ats_score: scores.atsScore,
          feedback,
          scored_by: user.id,
          iteration_number: iterationNumber
        });

      if (error) throw error;

      // Update resume review status
      const { error: updateError } = await supabase
        .from('resume_reviews')
        .update({ status: 'reviewed' })
        .eq('id', reviewId);

      if (updateError) throw updateError;

      toast.success('Resume scored successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error scoring resume:', error);
      toast.error('Failed to save score');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-black">Score Resume</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Keyword Score (25%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={scores.keywordScore}
                onChange={(e) => setScores(prev => ({ ...prev, keywordScore: parseInt(e.target.value) || 0 }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-white bg-[#2a1114]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Format Score (25%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={scores.formatScore}
                onChange={(e) => setScores(prev => ({ ...prev, formatScore: parseInt(e.target.value) || 0 }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-white bg-[#2a1114]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Content Score (30%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={scores.contentScore}
                onChange={(e) => setScores(prev => ({ ...prev, contentScore: parseInt(e.target.value) || 0 }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-white bg-[#2a1114]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-2">
                ATS Score (20%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={scores.atsScore}
                onChange={(e) => setScores(prev => ({ ...prev, atsScore: parseInt(e.target.value) || 0 }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-white bg-[#2a1114]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Total Score
              </label>
              <div className="text-2xl font-bold text-black">
                {calculateTotalScore()}%
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-black mb-2">
                Feedback
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg text-white bg-[#2a1114] h-32"
                placeholder="Provide detailed feedback about the resume..."
                required
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-[#2a1114] text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Submit Score'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}